import { Tabs, Badge, Group, Text, Button, Box, Loader, Modal, Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useNavigate } from 'react-router-dom';
import {
  IconForms,
  IconClipboard,
  IconWifi,
  IconWifiOff,
  IconPower,
  IconAlertTriangle,
  IconEye,
  IconCheck,
} from '@tabler/icons-react';
import { useAgentStore } from '@/stores/agentStore';
import { FieldsPanel } from './FieldsPanel';
import { SummaryPanel } from './SummaryPanel';

interface AgentTabsProps {
  roomCode: string;
  onEndMeeting: () => void;
  isConnected: boolean;
}

export const AgentTabs = ({ roomCode, onEndMeeting, isConnected }: AgentTabsProps) => {
  const navigate = useNavigate();
  const [confirmModalOpened, { open: openConfirmModal, close: closeConfirmModal }] = useDisclosure(false);
  const pendingSuggestions = useAgentStore((state) => state.pendingSuggestions);
  const summary = useAgentStore((state) => state.summary);
  const isEndingMeeting = useAgentStore((state) => state.isEndingMeeting);
  const processingStatus = useAgentStore((state) => state.processingStatus);

  const isMeetingEnded = !!summary;

  const handleConfirmEndMeeting = () => {
    closeConfirmModal();
    onEndMeeting();
  };

  const handleViewDetails = () => {
    navigate(`/meetings/${roomCode}`);
  };

  return (
    <Box style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Connection status and end meeting button */}
      <Box
        p="sm"
        style={{
          borderBottom: '1px solid var(--mantine-color-gray-3)',
          backgroundColor: 'var(--mantine-color-gray-0)',
        }}
      >
        <Group justify="space-between">
          <Group gap="md">
            <Group gap="xs">
              {isConnected ? (
                <>
                  <IconWifi size={16} color="var(--mantine-color-green-6)" />
                  <Text size="xs" c="green">
                    Copilot conectado
                  </Text>
                </>
              ) : (
                <>
                  <IconWifiOff size={16} color="var(--mantine-color-gray-5)" />
                  <Text size="xs" c="dimmed">
                    Copilot desconectado
                  </Text>
                </>
              )}
            </Group>
            {processingStatus.isProcessing && (
              <Group gap="xs">
                <Loader size={12} color="blue" />
                <Text size="xs" c="blue">
                  {processingStatus.nodeLabel}
                </Text>
              </Group>
            )}
          </Group>
          {isMeetingEnded ? (
            <>
              <Badge color="green" variant="light" leftSection={<IconCheck size={12} />}>
                Conversa encerrada
              </Badge>
              <Button
                size="xs"
                color="blue"
                variant="filled"
                leftSection={<IconEye size={14} />}
                onClick={handleViewDetails}
              >
                Ver detalhes
              </Button>
            </>
          ) : (
            <Button
              size="xs"
              color="red"
              variant="light"
              leftSection={isEndingMeeting ? <Loader size={14} color="red" /> : <IconPower size={14} />}
              onClick={openConfirmModal}
              disabled={!isConnected || isEndingMeeting}
            >
              {isEndingMeeting ? 'Encerrando...' : 'Encerrar Conversa'}
            </Button>
          )}
        </Group>
      </Box>

      {/* Tabs - now only 2: Campos and Resumo */}
      <Tabs
        defaultValue="fields"
        style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}
      >
        <Tabs.List>
          <Tabs.Tab
            value="fields"
            leftSection={<IconForms size={14} />}
            rightSection={
              pendingSuggestions.length > 0 ? (
                <Badge size="xs" variant="filled" color="blue">
                  {pendingSuggestions.length}
                </Badge>
              ) : null
            }
          >
            Campos
          </Tabs.Tab>
          <Tabs.Tab
            value="summary"
            leftSection={<IconClipboard size={14} />}
            rightSection={
              summary ? (
                <Badge size="xs" variant="filled" color="teal">
                  1
                </Badge>
              ) : null
            }
          >
            Resumo
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="fields" style={{ flex: 1, minHeight: 0 }}>
          <FieldsPanel roomCode={roomCode} />
        </Tabs.Panel>

        <Tabs.Panel value="summary" style={{ flex: 1, minHeight: 0 }}>
          <SummaryPanel />
        </Tabs.Panel>
      </Tabs>

      {/* Modal de confirmação para encerrar reunião */}
      <Modal
        opened={confirmModalOpened}
        onClose={closeConfirmModal}
        title={
          <Group gap="xs">
            <IconAlertTriangle size={20} color="var(--mantine-color-orange-6)" />
            <Text fw={600}>Encerrar Conversa</Text>
          </Group>
        }
        centered
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm">
            Tem certeza que deseja encerrar a conversa? O Copilot irá gerar um resumo final e não será mais possível adicionar novas informações.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={closeConfirmModal}>
              Cancelar
            </Button>
            <Button color="red" onClick={handleConfirmEndMeeting}>
              Encerrar Conversa
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
};
